/**
 * Room-first floor/storey detection — orchestrator.
 *
 * Pipeline: column z-histograms (one pass over the visible octree nodes) →
 * per-column surfaces → per-column room intervals → room patches → storeys.
 * See ./tuning.ts for every constant and ./types.ts for the result model.
 */

import * as THREE from 'three';
import type { PointCloudOctree } from '../../PointCloudOctree';
import { resolveTuning } from './tuning';
import type { ColumnInterval, FloorDetectionDebugData, StoreyDetectionOptions, StoreyDetectionResult } from './types';
import { accumulateNode, buildQuadEdges, collectNodesInBox, createColumnGrid } from './columnHistogram';
import { extractColumnSurfaces } from './surfaces';
import { extractColumnIntervals } from './intervals';
import { consensusFilterIntervals } from './roleConsensus';
import { buildRoomPatches } from './roomPatches';
import { clusterStoreys } from './storeys';
import { buildFloorDetectionDebugGroups } from './debugVisualization';

/** Subset of Viewer used by floor detection. */
export interface FloorDetectionViewer {
  scene: {
    pointclouds: PointCloudOctree[];
    scene: THREE.Scene;
  };
  /** Overlay scene rendered on top of the point cloud pass (debug surface layers). */
  measurementScene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  getBoundingBox(): THREE.Box3;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Progress split across the pipeline stages (0-100). */
const PROGRESS_ACCUMULATE_END = 70;
const PROGRESS_EXTRACT_END = 90;

/**
 * Detect all storeys (and sub-levels) inside the selection. Returns null when
 * cancelled via `options.shouldCancel`.
 */
export async function detectStoreys(viewer: FloorDetectionViewer, options: StoreyDetectionOptions): Promise<StoreyDetectionResult | null> {
  const tuning = resolveTuning(options.tuning);
  const { boundingBox, onProgress, shouldCancel } = options;

  const emptyDebug = (cellSize: number): FloorDetectionDebugData => ({ cellSize, surfaces: [], patches: [], patchStoreyIndex: [] });

  if (!viewer.scene.pointclouds || viewer.scene.pointclouds.length === 0) {
    console.warn('[floorDetection] No point clouds available');
    return { levels: [], debug: emptyDebug(tuning.CELL_SIZE_XY) };
  }

  onProgress?.(0);

  // ── Stage 1: column histograms ─────────────────────────────────────────
  const grid = createColumnGrid(boundingBox, tuning);
  const quadEdges = buildQuadEdges(options.areaCorners);
  const nodes = collectNodesInBox(viewer.scene.pointclouds, boundingBox);

  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    accumulateNode(grid, node.positions, node.count, node.matrixWorld, boundingBox, quadEdges);

    if ((i + 1) % tuning.YIELD_NODE_INTERVAL === 0 || i === nodes.length - 1) {
      onProgress?.((PROGRESS_ACCUMULATE_END * (i + 1)) / nodes.length);
      await sleep(0);
      if (shouldCancel?.()) return null;
    }
  }

  if (grid.totalPoints === 0) {
    console.warn('[floorDetection] No points inside the selected detection area');
    return { levels: [], debug: emptyDebug(grid.cellSize) };
  }

  // ── Stages 2+3: per-column surfaces and room intervals ─────────────────
  // (Replayed verbatim by fixture.ts:replayFloorDetectionFixture — keep the
  // stage sequence in sync.)
  const rawIntervalsByColumn: Array<ColumnInterval[] | null> = new Array(grid.columns.length).fill(null);
  const debug = emptyDebug(grid.cellSize);
  debug.grid = grid;
  debug.tuning = tuning;
  interface ColumnDebugSurfaces {
    column: number;
    surfaces: { z: number; role: (typeof debug.surfaces)[number]['role'] }[];
  }
  const debugColumns: ColumnDebugSurfaces[] = [];

  for (let column = 0; column < grid.columns.length; column++) {
    const bins = grid.columns[column];
    if (bins) {
      const surfaces = extractColumnSurfaces(bins, grid.binSize, grid.originZ, tuning);
      if (surfaces.length > 0) {
        const { intervals, roles } = extractColumnIntervals(surfaces, bins, grid.binSize, grid.originZ, tuning);
        if (intervals.length > 0) rawIntervalsByColumn[column] = intervals;

        if (debugColumns.length < tuning.DEBUG_MAX_SURFACES) {
          debugColumns.push({ column, surfaces: surfaces.map((s, i) => ({ z: s.z, role: roles[i] })) });
        }
      }
    }

    if ((column + 1) % tuning.YIELD_COLUMN_INTERVAL === 0 || column === grid.columns.length - 1) {
      onProgress?.(PROGRESS_ACCUMULATE_END + ((PROGRESS_EXTRACT_END - PROGRESS_ACCUMULATE_END) * (column + 1)) / grid.columns.length);
      await sleep(0);
      if (shouldCancel?.()) return null;
    }
  }

  // ── Stage 3.5: neighbourhood role consensus ────────────────────────────
  // A surface most neighbours call a ceiling can't be this column's floor
  // (and vice versa) — such mispaired intervals are dropped before patching.
  const { filtered: intervalsByColumn, droppedByColumn } = consensusFilterIntervals(rawIntervalsByColumn, grid.nx, grid.ny);

  // Emit debug surfaces with consensus applied: a floor/ceiling surface whose
  // interval was dropped shows as rejected.
  for (const entry of debugColumns) {
    const cx = grid.originX + ((entry.column % grid.nx) + 0.5) * grid.cellSize;
    const cy = grid.originY + (Math.floor(entry.column / grid.nx) + 0.5) * grid.cellSize;
    const dropped = droppedByColumn.get(entry.column);
    for (const s of entry.surfaces) {
      let role = s.role;
      if (dropped && (role === 'floor' || role === 'ceiling')) {
        const isDroppedEndpoint = dropped.some((iv) => Math.abs((role === 'floor' ? iv.floorZ : iv.ceilingZ) - s.z) <= grid.binSize);
        if (isDroppedEndpoint) role = 'rejected';
      }
      if (debug.surfaces.length < tuning.DEBUG_MAX_SURFACES) debug.surfaces.push({ x: cx, y: cy, z: s.z, role });
    }
  }

  // ── Stage 4: room patches ──────────────────────────────────────────────
  const patches = buildRoomPatches(intervalsByColumn, grid.nx, grid.ny, grid.cellSize, grid.originX, grid.originY, tuning);
  if (shouldCancel?.()) return null;

  // ── Stage 5: storeys ───────────────────────────────────────────────────
  // Footprints aligned with the drawn selection rectangle (when there is one).
  const frame = options.areaCorners && options.areaCorners.length >= 4 ? { areaCorners: options.areaCorners, grid: { nx: grid.nx, cellSize: grid.cellSize, originX: grid.originX, originY: grid.originY } } : undefined;
  const { levels, patchStoreyIndex } = clusterStoreys(patches, tuning, frame);
  debug.patches = patches;
  debug.patchStoreyIndex = patchStoreyIndex;

  onProgress?.(100);

  buildFloorDetectionDebugGroups(viewer, debug, levels);

  return { levels, debug };
}
